/**
 * readPreviewFeedback.js — shared public reader for preview feedback storage.
 *
 * Canonical source for reading `kea_storefront_preview_feedback_v1` from
 * localStorage. Shared between hook consumers and any admin-side feature that
 * needs to inspect normalized preview feedback.
 *
 * Source contract: 07-preview-storage-and-feedback-contract.md
 *
 * Reading rules:
 *   - Read ONLY from the public key kea_storefront_preview_feedback_v1.
 *   - Expose only the public AdminPreviewIssue shape fields.
 *   - Mark feedback as stale when updated_at is older than FEEDBACK_STALE_MS.
 *   - Strip private context keys via PUBLIC_CONTEXT_KEYS allowlist.
 */

// Public storage key — must match kea-static-resources previewContract.js
export const PREVIEW_FEEDBACK_KEY = 'kea_storefront_preview_feedback_v1';
export const PREVIEW_FEEDBACK_VERSION = 1;

// Staleness threshold: 15 minutes (doc-07 §4)
export const FEEDBACK_STALE_MS = 15 * 60 * 1000;

/**
 * Canonical normalized source tokens for preview issues.
 *
 * The runtime (kea-static-resources resolveCardTemplate.js) dispatches
 * 'product_card_template' at the call site, but previewStorage.js calls
 * normalizePreviewIssueSource() before writing to localStorage which maps
 * that token to 'template'. All stored issues therefore carry 'template'.
 * Admin readers MUST filter by the stored normalized form, not the raw token.
 *
 * Reference: kea-static-resources/src/preact/components/preview/previewContract.js
 *   ISSUE_SOURCE_TEMPLATE = 'template'
 *   normalization: { product_card_template → 'template', template → 'template' }
 */
export const PREVIEW_ISSUE_SOURCES = {
  // Canonical stored token (post-normalization). Use this for filtering stored issues.
  TEMPLATE: 'template',
};

// Allowed public context keys (must match adminPreviewFeedback.js allowlist)
const PUBLIC_CONTEXT_KEYS = new Set([
  'template_id',
  'template_hash',
  'search_box_id',
  'view',
  'runtime_type',
  'selector',
  'selector_source',
  'source_id',
  'status',
]);

function isFeedbackStale(updatedAt) {
  if (!updatedAt) return true;
  try {
    const age = Date.now() - new Date(updatedAt).getTime();
    return Number.isNaN(age) || age > FEEDBACK_STALE_MS;
  } catch {
    return true;
  }
}

function projectContext(context) {
  if (!context || typeof context !== 'object') return {};
  return Object.fromEntries(
    Object.entries(context).filter(([key]) => PUBLIC_CONTEXT_KEYS.has(key)),
  );
}

function projectIssues(issues) {
  if (!Array.isArray(issues)) return [];
  return issues.map((issue) => ({
    id: issue.id || '',
    dedupe_key: issue.dedupe_key || '',
    timestamp: issue.timestamp || '',
    severity: issue.severity === 'error' ? 'error' : 'warning',
    code: issue.code || '',
    source: issue.source || 'unknown',
    message: issue.message || '',
    context: projectContext(issue.context),
  }));
}

/**
 * Read and parse preview feedback from localStorage.
 *
 * Returns null when storage is unavailable, key is absent, data cannot be
 * parsed, or there is a version mismatch.
 *
 * @returns {{ version, updated_at, preview_enabled, mode, warnings, errors, has_draft_settings, stale } | null}
 */
export function readPreviewFeedback() {
  let raw;
  try {
    raw = window.localStorage.getItem(PREVIEW_FEEDBACK_KEY);
  } catch {
    return null;
  }

  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || parsed.version !== PREVIEW_FEEDBACK_VERSION) return null;

  return {
    version: parsed.version,
    updated_at: parsed.updated_at || '',
    preview_enabled: parsed.preview_enabled === true,
    mode: typeof parsed.mode === 'string' ? parsed.mode : 'unknown',
    warnings: projectIssues(parsed.warnings),
    errors: projectIssues(parsed.errors),
    has_draft_settings: parsed.has_draft_settings === true,
    stale: isFeedbackStale(parsed.updated_at),
  };
}
